/**
 * ToastProvider Component
 * React Context for managing toast notifications across the app
 * Uses Shopify Polaris Frame and Toast components
 * Includes ARIA live region announcements for screen reader accessibility
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { Toast } from '@shopify/polaris';
import { LiveRegion } from '../core/LiveRegion';

// =============================================================================
// Types
// =============================================================================

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  content: string;
  variant?: ToastVariant;
  duration?: number;
  action?: {
    content: string;
    onAction: () => void;
  };
}

interface ToastContextValue {
  showToast: (message: Omit<ToastMessage, 'id'>) => void;
  hideToast: (id: string) => void;
  clearAll: () => void;
  toasts: ToastMessage[];
}

// =============================================================================
// Context
// =============================================================================

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// =============================================================================
// Provider Component
// =============================================================================

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  /**
   * Show a new toast notification
   */
  const showToast = useCallback((message: Omit<ToastMessage, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const duration = message.duration || 3000;

    const newToast: ToastMessage = {
      ...message,
      id,
      duration,
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto-dismiss after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  /**
   * Hide a specific toast by ID
   */
  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  /**
   * Clear all toasts
   */
  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast, clearAll, toasts }}>
      {children}
    </ToastContext.Provider>
  );
}

// =============================================================================
// Toast Renderer Component (to be used inside Frame)
// =============================================================================

export function ToastRenderer() {
  const { toasts, hideToast } = useToast();
  const [announcement, setAnnouncement] = useState('');

  /**
   * Map variant to Polaris Toast error prop
   */
  const getToastError = (variant?: ToastVariant): boolean => {
    return variant === 'error' || variant === 'warning';
  };

  /**
   * Map variant to ARIA live region politeness level
   */
  const getPoliteness = (variant?: ToastVariant): 'polite' | 'assertive' => {
    return variant === 'error' || variant === 'warning' ? 'assertive' : 'polite';
  };

  /**
   * Announce new toasts to screen readers
   */
  useEffect(() => {
    if (toasts.length > 0) {
      const latestToast = toasts[toasts.length - 1];
      // Prefix variant for context
      const prefix = latestToast.variant ? `${latestToast.variant}: ` : '';
      setAnnouncement(`${prefix}${latestToast.content}`);
    }
  }, [toasts]);

  return (
    <>
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          content={toast.content}
          error={getToastError(toast.variant)}
          onDismiss={() => hideToast(toast.id)}
          action={toast.action}
        />
      ))}
      <LiveRegion
        message={announcement}
        politeness={toasts.length > 0 ? getPoliteness(toasts[toasts.length - 1].variant) : 'polite'}
      />
    </>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// =============================================================================
// Convenience Methods
// =============================================================================

export function useToastHelpers() {
  const { showToast } = useToast();

  return {
    success: (content: string, action?: ToastMessage['action']) =>
      showToast({ content, variant: 'success', action }),

    error: (content: string, action?: ToastMessage['action']) =>
      showToast({ content, variant: 'error', action }),

    info: (content: string, action?: ToastMessage['action']) =>
      showToast({ content, variant: 'info', action }),

    warning: (content: string, action?: ToastMessage['action']) =>
      showToast({ content, variant: 'warning', action }),
  };
}
